/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { differenceInYears, format, parse } from 'date-fns';
import { IoIosArrowBack } from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import {
  Input,
  Select,
  Layout,
  Textarea,
  InputMask,
  InputRadio,
} from '~/components';
import { useScreen } from '~/hooks/screen';
import { useStudent } from '~/hooks/student';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';
import { imcCalc } from '~/utils/imcCalc';

import {
  studentLevelOptions,
  objectiveOptions,
  frequencyOptions,
  availableTimeOptions,
  scheduleOptions,
} from './constants';
import { getProfileCode } from './utils';

import {
  Container,
  PageHeader,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  Sidebar,
  Wrapper,
  PageHeaderSaveButton,
  FormContainer,
  HideForm,
  FormLineWrapper,
  FieldSet,
  Label,
  CheckboxWrapper,
  SectionLabel,
  DoubleInputContainer,
  SelectWrapper,
  RadioButton,
  ProfileCodeContainer,
  TextareaSet,
  SessionContainer,
  CheckBoxSide,
} from './styles';

interface RouteParams {
  userId: string;
}

type DataSheet = {
  id: number;
  student_id: number;
  professional_id: number;
  gym_id?: string;
  genre?: string;
  classification: string;
  objective: string;
  student_level: string;
  frequency: string;
  available_time: string;
  training_division?: string;
  week_days: string;
  health_history: string;
  injury_history: string;
  height: number;
  weight: number;
  goal_weight: number;
  body_fat: number;
  imc: number;
  birth_date: string;
  circumference_chest: number;
  circumference_waist: number;
  circumference_abdomen: number;
  circumference_hip: number;
  skin_folds_total: number;
  created_at: string;
  updated_at: string;
  time_preference: string;
  circumference_relaxed_arm_right: number;
  circumference_relaxed_arm_left: number;
  circumference_contracted_arm_right: number;
  circumference_contracted_arm_left: number;
  circumference_forearm_right: number;
  circumference_forearm_left: number;
  circumference_fist_right: number;
  circumference_fist_left: number;
  circumference_proximal_thigh_right: number;
  circumference_proximal_thigh_left: number;
  circumference_distal_thigh_right: number;
  circumference_distal_thigh_left: number;
  circumference_calf_right: number;
  circumference_calf_left: number;
  set_left_side?: string;
  set_right_side?: string;
  skin_folds_biceps_right: number;
  skin_folds_biceps_left: number;
  skin_folds_triceps_right: number;
  skin_folds_triceps_left: number;
  skin_folds_subscapular_right: number;
  skin_folds_subscapular_left: number;
  skin_folds_middle_armpit_right: number;
  skin_folds_middle_armpit_left: number;
  skin_folds_heigsupra_iliacaht_right: number;
  skin_folds_heigsupra_iliacaht_left: number;
  skin_folds_abdomen_right: number;
  skin_folds_abdomen_left: number;
  skin_folds_breastplate_right: number;
  skin_folds_breastplate_left: number;
  skin_folds_thigh_right: number;
  skin_folds_thigh_left: number;
  skin_folds_calf_right: number;
  skin_folds_calf_left: number;
  profile_code: string;
};

type IMCData = {
  imc: number;
  classification: string;
};

const maleBodyDensityConsts = {
  const1: 1.112,
  const2: 0.00043499,
  const3: 0.00000055,
  const4: 0.00028826,
  const5: 4.95,
  const6: 4.5,
};

const femaleBodyDensityConsts = {
  const1: 1.097,
  const2: 0.00046971,
  const3: 0.00000056,
  const4: 0.00012828,
  const5: 5.01,
  const6: 4.57,
};

const DataSheetRegister: React.FC = () => {
  const { userId: studentId } = useParams<RouteParams>();
  const { goBack } = useHistory();
  const { studentData, updateStudent } = useStudent();
  const { updateScreen } = useScreen();
  const formRef = useRef<FormHandles>(null);

  const [skinFoldSide, setSkinFoldSide] = useState<'left' | 'right'>('left');

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedBox, setSelectedBox] = useState<string[]>(['SEGUNDA']);
  const [selectedComponent, setSelectedComponent] = useState('dataSheet');
  const [height, setHeight] = useState<string>();
  const [weight, setWeight] = useState<string>();
  const [studentLevel, setStudentLevel] = useState<string>(
    studentLevelOptions[0].value,
  );
  const [frequency, setFrequency] = useState<string>(frequencyOptions[0].value);
  const [availableTime, setAvailableTime] = useState<string>(
    availableTimeOptions[0].value,
  );
  const [objective, setObjective] = useState<string>(objectiveOptions[0].value);
  const [IMC, setIMC] = useState<IMCData>();
  const [profileCode, setProfileCode] = useState<string>();

  // Atualiza dados do aluno no hook caso não estejam lá
  useEffect(() => {
    updateStudent(studentId);
    updateScreen(`dataSheet`);
  }, [studentId, updateStudent, updateScreen]);

  // Update do imc
  useEffect(() => {
    setIMC(
      imcCalc({
        h: height?.replaceAll(',', '.') || ``,
        w: weight?.replaceAll(',', '.') || ``,
      }),
    );
  }, [height, weight]);

  // Update do profileCode
  useEffect(() => {
    setProfileCode(
      getProfileCode({
        studentLevel,
        frequency,
        gender: studentData?.student?.genre,
        availableTime,
        physicalEvaluation: IMC?.classification || '',
        objective,
      }),
    );
  }, [studentLevel, frequency, studentData, availableTime, IMC, objective]);

  const checkBoxOptions = useMemo(() => {
    return [
      { id: 'SEGUNDA', label: 'Segunda' },
      { id: 'TERÇA', label: 'Terça' },
      { id: 'QUARTA', label: 'Quarta' },
      { id: 'QUINTA', label: 'Quinta' },
      { id: 'SEXTA', label: 'Sexta' },
      { id: 'SABADO', label: 'Sábado' },
      { id: 'DOMINGO', label: 'Domingo' },
    ];
  }, []);

  const studentAge = useMemo(() => {
    const date = parse(
      studentData.student.birth_date,
      'yyyy-mm-dd',
      new Date(),
    );

    const age = differenceInYears(new Date(), date);
    return age;
  }, [studentData]);

  const handleSelectedCheckbox = useCallback(
    (id: string) => {
      const frequencyValue = parseInt(frequency.split('')[0], 10);

      if (selectedBox.includes(id) && selectedBox.length === 1) return;

      if (selectedBox.length === 1 && frequencyValue === 1) {
        setSelectedBox([id]);
        return;
      }

      if (selectedBox.length < frequencyValue) {
        const newValues = [];

        if (selectedBox.includes(id) && selectedBox.length < frequencyValue) {
          let valuesWithOldArray = [...selectedBox];

          const filteredValues = selectedBox.filter((item) => item !== id);
          valuesWithOldArray = filteredValues;

          setSelectedBox(valuesWithOldArray as string[]);
        } else {
          selectedBox.includes(id)
            ? newValues.push(...[selectedBox.filter((item) => item !== id)])
            : newValues.push(...[...selectedBox, id]);

          setSelectedBox(newValues as string[]);
        }
      } else {
        let valuesWithOldArray2 = [...selectedBox];

        if (selectedBox.includes(id)) {
          const filteredValues = selectedBox.filter((item) => item !== id);
          valuesWithOldArray2 = filteredValues;
        }

        setSelectedBox(valuesWithOldArray2 as string[]);
      }
    },
    [selectedBox, frequency],
  );

  useEffect(() => {
    const frequencyValue = parseInt(frequency.split('')[0], 10);

    if (frequencyValue === 7) {
      setSelectedBox(() => checkBoxOptions.map((value) => value.id));
    }

    if (selectedBox.length > frequencyValue) {
      const filteredBox: string[] = [];

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < frequencyValue; i++) {
        filteredBox.push(selectedBox[i]);
      }

      setSelectedBox(filteredBox);
    }
  }, [frequency, selectedBox, checkBoxOptions]);

  const handleSumSkinFoldValues = (leftSide?: string, rightSide?: string) => {
    const leftSideValue = leftSide || '0';
    const rightSideValue = rightSide || '0';

    const convertedLeftSideValue = parseInt(leftSideValue, 10);
    const convertedRightSideValue = parseInt(rightSideValue, 10);

    const divider =
      convertedLeftSideValue === 0 || convertedRightSideValue === 0 ? 1 : 2;

    return (convertedRightSideValue + convertedLeftSideValue) / divider;
  };

  // ? Cálculo de dobras cutâneas
  const handleFormChange = () => {
    const {
      skin_folds_biceps_right,
      skin_folds_biceps_left,
      skin_folds_triceps_right,
      skin_folds_triceps_left,
      skin_folds_subscapular_right,
      skin_folds_subscapular_left,
      skin_folds_middle_armpit_right,
      skin_folds_middle_armpit_left,
      skin_folds_heigsupra_iliacaht_right,
      skin_folds_heigsupra_iliacaht_left,
      skin_folds_abdomen_right,
      skin_folds_abdomen_left,
      skin_folds_breastplate_right,
      skin_folds_breastplate_left,
      skin_folds_thigh_right,
      skin_folds_thigh_left,
      skin_folds_calf_right,
      skin_folds_calf_left,
    }: any = formRef?.current?.getData();

    const skinFoldsTotalValue = [
      handleSumSkinFoldValues(skin_folds_biceps_left, skin_folds_biceps_right),
      handleSumSkinFoldValues(
        skin_folds_triceps_left,
        skin_folds_triceps_right,
      ),
      handleSumSkinFoldValues(
        skin_folds_subscapular_left,
        skin_folds_subscapular_right,
      ),
      handleSumSkinFoldValues(
        skin_folds_middle_armpit_left,
        skin_folds_middle_armpit_right,
      ),
      handleSumSkinFoldValues(
        skin_folds_heigsupra_iliacaht_left,
        skin_folds_heigsupra_iliacaht_right,
      ),
      handleSumSkinFoldValues(
        skin_folds_abdomen_left,
        skin_folds_abdomen_right,
      ),
      handleSumSkinFoldValues(
        skin_folds_breastplate_left,
        skin_folds_breastplate_right,
      ),
      handleSumSkinFoldValues(skin_folds_thigh_left, skin_folds_thigh_right),
      handleSumSkinFoldValues(skin_folds_calf_left, skin_folds_calf_right),
    ];

    const skinFoldsTotalValuesSummed = skinFoldsTotalValue.reduce(
      (acc, current) => acc + current,
    );

    const bodyDensityConstants =
      studentData.student.genre === 'female'
        ? femaleBodyDensityConsts
        : maleBodyDensityConsts;

    const firstPart =
      bodyDensityConstants.const1 -
      bodyDensityConstants.const2 * skinFoldsTotalValuesSummed;

    const secondPart =
      bodyDensityConstants.const3 * skinFoldsTotalValuesSummed ** 2;

    const thirdPart = bodyDensityConstants.const4 * studentAge;

    const bodyDensity = firstPart + secondPart - thirdPart;

    const bodyFatPercentage =
      (bodyDensityConstants.const5 / bodyDensity -
        bodyDensityConstants.const6) *
      100;

    if (!skinFoldsTotalValue.includes(0)) {
      formRef.current?.setFieldValue(
        'skin_folds_total',
        bodyFatPercentage.toFixed(2),
      );
    } else {
      formRef.current?.setFieldValue('skin_folds_total', null);
    }
  };

  const CheckBox = useMemo(
    () =>
      checkBoxOptions.map((item) => (
        <RadioButton
          key={item.id}
          isSelected={selectedBox.includes(item.id)}
          onClick={() => handleSelectedCheckbox(item.id)}
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </RadioButton>
      )),
    [checkBoxOptions, selectedBox, frequency, handleSelectedCheckbox],
  );

  const inputStyle = {
    input: {
      height: 45,
      marginTop: 5,
      padding: '0 1.7rem',
    },
    textArea: {
      width: '100%',
      marginTop: 5,

      height: '19rem',
      padding: '2rem',
      borderRadius: '1rem',
    },
    doubleInput: {
      height: 45,
      marginTop: 5,

      padding: '0 1.7rem',
    },
    select: {
      placeholder: () => ({
        padding: '0 1rem',
        color: '#00000080',
      }),
      singleValue: () => ({
        padding: '0 1rem',
      }),
    },
  };

  const handleSubmit = useCallback(
    async (data: DataSheet) => {
      setLoading(true);

      const formData = {
        ...data,
        student_id: studentId,
        birth_date: format(
          new Date(studentData?.student?.birth_date),
          'yyyy-MM-dd',
        ),
        height: String(data.height).replaceAll(',', '.'),
        weight: String(data.weight).replaceAll(',', '.'),
        goal_weight: String(data.goal_weight).replaceAll(',', '.'),
        body_fat: String(data.body_fat).replaceAll(',', '.'),
        genre: studentData?.student?.genre,
        week_days: selectedBox.join(','),
        health_history:
          data.health_history || 'Prefiro não incluir histórico de saúde',
        injury_history:
          data.injury_history || 'Prefiro não incluir histórico de lesão',
      };

      delete formData.set_left_side;
      delete formData.set_right_side;

      const schema = Yup.object().shape({
        student_id: Yup.number().required(),
        birth_date: Yup.string().required(),
        height: Yup.number().required('O campo é obrigatório'),
        weight: Yup.number().required('O campo é obrigatório'),
        imc: Yup.number().required('O campo é obrigatório'),
        body_fat: Yup.number().required('O campo é obrigatório'),
        goal_weight: Yup.number().required('O campo é obrigatório'),
      });

      try {
        await schema.validate(formData, { abortEarly: false });

        await api.post('datasheets', formData);

        toast.success('Ficha técnica cadastrada com sucesso!');

        goBack();
      } catch (err) {
        console.log(err);
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        toast.error('Erro ao cadastrar ficha técnica!');
      }
    },
    [studentId, studentData, selectedBox, goBack],
  );

  const sidebarList = [
    {
      value: 'dataSheet',
      title: 'Ficha técnica',
    },
    {
      value: 'anthropometry',
      title: 'Antropometria',
    },
  ];

  const renderSkinFoldSide = useCallback(() => {
    return (
      <CheckBoxSide>
        <section>
          <InputRadio
            name="set_left_side"
            id="set_left_side"
            checked={skinFoldSide === 'left'}
            onClick={() => setSkinFoldSide('left')}
          />
          <label>Lado esquerdo</label>
        </section>

        <section>
          <InputRadio
            name="set_right_side"
            id="set_right_side"
            checked={skinFoldSide === 'right'}
            onClick={() => setSkinFoldSide('right')}
          />
          <label>Lado direito</label>
        </section>
      </CheckBoxSide>
    );
  }, [skinFoldSide]);

  const renderLeftSideSkinFoldsForm = useCallback(() => {
    return (
      <FormContainer>
        <FieldSet>
          <DoubleInputContainer>
            <div>
              <Label>Bíceps (mm):</Label>
              <Input
                name="skin_folds_biceps_left"
                placeholder="Lado esquerdo"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
            <div>
              <Label>Tríceps (mm):</Label>
              <Input
                name="skin_folds_triceps_left"
                placeholder="Lado esquerdo"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
          </DoubleInputContainer>
        </FieldSet>

        <FieldSet>
          <DoubleInputContainer>
            <div>
              <Label>Subescapular (mm):</Label>
              <Input
                name="skin_folds_subscapular_left"
                placeholder="Lado esquerdo"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
            <div>
              <Label>Axila média (mm):</Label>
              <Input
                name="skin_folds_middle_armpit_left"
                placeholder="Lado esquerdo"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
          </DoubleInputContainer>
        </FieldSet>

        <FieldSet>
          <DoubleInputContainer>
            <div>
              <Label>Supra ilíaca (mm):</Label>
              <Input
                name="skin_folds_heigsupra_iliacaht_left"
                placeholder="Lado esquerdo"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
            <div>
              <Label>Abdomen (mm):</Label>
              <Input
                name="skin_folds_abdomen_left"
                placeholder="Lado esquerdo"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
          </DoubleInputContainer>
        </FieldSet>

        <FieldSet>
          <DoubleInputContainer>
            <div>
              <Label>Peitoral (mm):</Label>
              <Input
                name="skin_folds_breastplate_left"
                placeholder="Lado esquerdo"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
            <div>
              <Label>Coxa (mm):</Label>
              <Input
                name="skin_folds_thigh_left"
                placeholder="Lado esquerdo"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
          </DoubleInputContainer>
        </FieldSet>

        <FieldSet>
          <Label>Panturrilha (mm):</Label>
          <Input
            name="skin_folds_calf_left"
            placeholder="Lado esquerdo"
            containerStyle={inputStyle.doubleInput}
            fontSize="1.5rem"
          />
        </FieldSet>

        <FieldSet>
          <Label>Total lado esquerdo (mm):</Label>
          <Input
            name="skin_folds_total"
            containerStyle={inputStyle.input}
            fontSize="1.5rem"
          />
        </FieldSet>
      </FormContainer>
    );
  }, [skinFoldSide]);

  const renderRightSideSkinFoldsForm = useCallback(() => {
    return (
      <FormContainer>
        <FieldSet>
          <DoubleInputContainer>
            <div>
              <Label>Bíceps (mm):</Label>
              <Input
                name="skin_folds_biceps_right"
                placeholder="Lado direito"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
            <div>
              <Label>Tríceps (mm):</Label>
              <Input
                name="skin_folds_triceps_right"
                placeholder="Lado direito"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
          </DoubleInputContainer>
        </FieldSet>

        <FieldSet>
          <DoubleInputContainer>
            <div>
              <Label>Subescapular (mm):</Label>
              <Input
                name="skin_folds_subscapular_right"
                placeholder="Lado direito"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
            <div>
              <Label>Axila média (mm):</Label>
              <Input
                name="skin_folds_middle_armpit_right"
                placeholder="Lado direito"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
          </DoubleInputContainer>
        </FieldSet>

        <FieldSet>
          <DoubleInputContainer>
            <div>
              <Label>Supra ilíaca (mm):</Label>
              <Input
                name="skin_folds_heigsupra_iliacaht_right"
                placeholder="Lado direito"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
            <div>
              <Label>Abdomen (mm):</Label>
              <Input
                name="skin_folds_abdomen_right"
                placeholder="Lado direito"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
          </DoubleInputContainer>
        </FieldSet>

        <FieldSet>
          <DoubleInputContainer>
            <div>
              <Label>Peitoral (mm):</Label>
              <Input
                name="skin_folds_breastplate_right"
                placeholder="Lado direito"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
            <div>
              <Label>Coxa (mm):</Label>
              <Input
                name="skin_folds_thigh_right"
                placeholder="Lado direito"
                containerStyle={inputStyle.doubleInput}
                fontSize="1.5rem"
              />
            </div>
          </DoubleInputContainer>
        </FieldSet>

        <FieldSet>
          <Label>Panturrilha (mm):</Label>
          <Input
            name="skin_folds_calf_right"
            placeholder="Lado direito"
            containerStyle={inputStyle.doubleInput}
            fontSize="1.5rem"
          />
        </FieldSet>

        <FieldSet>
          <Label>Total lado direito (mm):</Label>
          <Input
            name="skin_folds_total"
            containerStyle={inputStyle.input}
            fontSize="1.5rem"
          />
        </FieldSet>
      </FormContainer>
    );
  }, [skinFoldSide]);

  const handleChangeMember = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value = event.target.value.replace(/[^0-9,]/g, '');
  };

  return (
    <Layout title="Aluno">
      <Container>
        <Sidebar>
          <ul>
            {sidebarList.map((item) => (
              <li
                key={item.value}
                onClick={() => setSelectedComponent(item.value)}
                className={selectedComponent === item.value ? 'active' : ''}
              >
                <p>{item.title}</p>
              </li>
            ))}
          </ul>
        </Sidebar>

        <Wrapper>
          <PageHeader>
            <PageHeaderBackButton onClick={() => goBack()}>
              <IoIosArrowBack />
              <PageHeaderBackButtonLabel>
                nova ficha técnica
              </PageHeaderBackButtonLabel>
            </PageHeaderBackButton>

            <PageHeaderSaveButton
              disabled={loading}
              onClick={() => formRef.current?.submitForm()}
            >
              Salvar
            </PageHeaderSaveButton>
          </PageHeader>

          <SessionContainer>
            <Form
              ref={formRef}
              onSubmit={handleSubmit}
              onChange={handleFormChange}
            >
              <HideForm hide={selectedComponent !== 'dataSheet'}>
                <FormContainer>
                  <FieldSet>
                    <Label>Altura:</Label>
                    <InputMask
                      mask="9,99"
                      name="height"
                      placeholder="1,75"
                      onChange={(e) => setHeight(e.target.value)}
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                    />
                  </FieldSet>

                  <FieldSet>
                    <Label>Peso:</Label>
                    <Input
                      name="weight"
                      placeholder="99,9"
                      onChange={(e) => {
                        handleChangeMember(e);
                        setWeight(e.target.value);
                      }}
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                      type="text"
                    />
                  </FieldSet>

                  <FieldSet>
                    <Label>IMC:</Label>
                    <Input
                      name="imc"
                      placeholder="Esperando altura..."
                      disabled
                      value={IMC?.imc || ''}
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                    />
                  </FieldSet>

                  <FieldSet>
                    <Label>% Fat:</Label>
                    <Input
                      name="body_fat"
                      placeholder="28"
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                      onChange={handleChangeMember}
                    />
                  </FieldSet>

                  <FieldSet>
                    <Label>Classificação:</Label>
                    <Input
                      name="classification"
                      placeholder="Esperando o IMC..."
                      value={IMC?.classification}
                      disabled
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                    />
                  </FieldSet>

                  <FieldSet>
                    <Label>Meta de peso:</Label>
                    <Input
                      name="goal_weight"
                      placeholder="75"
                      onChange={handleChangeMember}
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                    />
                  </FieldSet>

                  <FieldSet>
                    <Label>Nível do aluno:</Label>
                    <SelectWrapper>
                      <Select
                        name="student_level"
                        isSearchable={false}
                        placeholder="Selecione"
                        onChange={(e) => setStudentLevel(e?.value)}
                        defaultValue={studentLevelOptions[0]}
                        options={studentLevelOptions}
                        styles={inputStyle.select}
                      />
                    </SelectWrapper>
                  </FieldSet>

                  <FieldSet>
                    <Label>Objetivo:</Label>
                    <SelectWrapper>
                      <Select
                        name="objective"
                        isSearchable={false}
                        placeholder="Selecione"
                        onChange={(e) => setObjective(e?.value)}
                        defaultValue={objectiveOptions[0]}
                        options={objectiveOptions}
                        styles={inputStyle.select}
                      />
                    </SelectWrapper>
                  </FieldSet>

                  <FieldSet>
                    <Label>Frequência:</Label>
                    <SelectWrapper>
                      <Select
                        name="frequency"
                        isSearchable={false}
                        placeholder="Selecione"
                        onChange={(e) => setFrequency(e?.value)}
                        defaultValue={frequencyOptions[0]}
                        options={frequencyOptions}
                        styles={inputStyle.select}
                      />
                    </SelectWrapper>
                  </FieldSet>

                  <FieldSet>
                    <Label>Tempo disponível:</Label>
                    <SelectWrapper>
                      <Select
                        name="available_time"
                        isSearchable={false}
                        placeholder="Selecione"
                        onChange={(e) => setAvailableTime(e?.value)}
                        defaultValue={availableTimeOptions[0]}
                        options={availableTimeOptions}
                        styles={inputStyle.select}
                      />
                    </SelectWrapper>
                  </FieldSet>

                  <FieldSet>
                    <Label>Dias da semana:</Label>

                    <CheckboxWrapper>{CheckBox}</CheckboxWrapper>
                  </FieldSet>

                  <FieldSet>
                    <Label>Preferência de horário:</Label>
                    <SelectWrapper>
                      <Select
                        name="time_preference"
                        placeholder="Selecione"
                        isSearchable={false}
                        options={scheduleOptions}
                        defaultValue={scheduleOptions[0]}
                        styles={inputStyle.select}
                      />
                    </SelectWrapper>
                  </FieldSet>
                </FormContainer>

                <FormLineWrapper>
                  <TextareaSet>
                    <Label>Histórico de saúde:</Label>
                    <Textarea
                      style={{ fontSize: '14px' }}
                      name="health_history"
                      placeholder="Prefiro não incluir histórico de saúde"
                      containerStyle={inputStyle.textArea}
                    />
                  </TextareaSet>
                </FormLineWrapper>

                <FormLineWrapper>
                  <TextareaSet>
                    <Label>Histórico de lesão:</Label>
                    <Textarea
                      style={{ fontSize: '14px' }}
                      name="injury_history"
                      placeholder="Prefiro não incluir histórico de lesão"
                      containerStyle={inputStyle.textArea}
                    />
                  </TextareaSet>
                </FormLineWrapper>

                <FormLineWrapper>
                  <ProfileCodeContainer>
                    <Label>Profile Code:</Label>
                    <Input
                      name="profile_code"
                      disabled
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                      value={
                        profileCode
                          ?.replace(/(.{4})/g, ' - $1')
                          ?.substr(2, 40) || ''
                      }
                    />
                  </ProfileCodeContainer>
                </FormLineWrapper>
              </HideForm>

              <HideForm hide={selectedComponent !== 'anthropometry'}>
                <SectionLabel>Circunferências</SectionLabel>

                <FormContainer>
                  <FieldSet>
                    <Label>Tórax (cm):</Label>
                    <Input
                      name="circumference_chest"
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                    />
                  </FieldSet>

                  <FieldSet>
                    <Label>Cintura (cm):</Label>
                    <Input
                      name="circumference_waist"
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                    />
                  </FieldSet>

                  <FieldSet>
                    <Label>Abdomen (cm):</Label>
                    <Input
                      name="circumference_abdomen"
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                    />
                  </FieldSet>

                  <FieldSet>
                    <Label>Quadril (cm):</Label>
                    <Input
                      name="circumference_hip"
                      containerStyle={inputStyle.input}
                      fontSize="1.5rem"
                    />
                  </FieldSet>

                  <FieldSet>
                    <Label>Braço relaxado (cm):</Label>
                    <DoubleInputContainer>
                      <Input
                        name="circumference_relaxed_arm_left"
                        placeholder="Lado direito"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />

                      <Input
                        name="circumference_relaxed_arm_right"
                        placeholder="Lado direito"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                    </DoubleInputContainer>
                  </FieldSet>

                  <FieldSet>
                    <Label>Braço contraído (cm):</Label>
                    <DoubleInputContainer>
                      <Input
                        name="circumference_contracted_arm_left"
                        placeholder="Lado esquerdo"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                      <Input
                        name="circumference_contracted_arm_right"
                        placeholder="Lado direito"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                    </DoubleInputContainer>
                  </FieldSet>

                  <FieldSet>
                    <Label>Antebraço (cm):</Label>
                    <DoubleInputContainer>
                      <Input
                        name="circumference_forearm_left"
                        placeholder="Lado esquerdo"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                      <Input
                        name="circumference_forearm_right"
                        placeholder="Lado direito"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                    </DoubleInputContainer>
                  </FieldSet>

                  <FieldSet>
                    <Label>Punho (cm):</Label>
                    <DoubleInputContainer>
                      <Input
                        name="circumference_fist_left"
                        placeholder="Lado esquerdo"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                      <Input
                        name="circumference_fist_right"
                        placeholder="Lado direito"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                    </DoubleInputContainer>
                  </FieldSet>

                  <FieldSet>
                    <Label>Coxa proximal - prega glútea (cm):</Label>
                    <DoubleInputContainer>
                      <Input
                        name="circumference_proximal_thigh_left"
                        placeholder="Lado esquerdo"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                      <Input
                        name="circumference_proximal_thigh_right"
                        placeholder="Lado direito"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                    </DoubleInputContainer>
                  </FieldSet>

                  <FieldSet>
                    <Label>Coxa distal - 10 cm (cm):</Label>
                    <DoubleInputContainer>
                      <Input
                        name="circumference_distal_thigh_left"
                        placeholder="Lado esquerdo"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                      <Input
                        name="circumference_distal_thigh_right"
                        placeholder="Lado direito"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                    </DoubleInputContainer>
                  </FieldSet>

                  <FieldSet>
                    <Label>Panturrilha (cm):</Label>
                    <DoubleInputContainer>
                      <Input
                        name="circumference_calf_left"
                        placeholder="Lado esquerdo"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                      <Input
                        name="circumference_calf_right"
                        placeholder="Lado direito"
                        containerStyle={inputStyle.doubleInput}
                        fontSize="1.5rem"
                      />
                    </DoubleInputContainer>
                  </FieldSet>
                </FormContainer>

                <SectionLabel secondary>
                  dobras cutâneas {renderSkinFoldSide()}
                </SectionLabel>

                {skinFoldSide === 'left'
                  ? renderLeftSideSkinFoldsForm()
                  : renderRightSideSkinFoldsForm()}
              </HideForm>
            </Form>
          </SessionContainer>
        </Wrapper>
      </Container>
    </Layout>
  );
};

export default DataSheetRegister;
